"use client";

import { useLanguage } from "@/components/LanguageProvider";
import { cn } from "@/lib/utils";
import type { FAQ } from "@/lib/types";

export type FAQAccordionItem = {
  id?: number | string;
  question: string;
  answer: string;
};

type Props = {
  heading?: string;
  subheading?: string;
  items?: FAQAccordionItem[];
  faqs?: FAQ[];
  className?: string;
};

export function FAQAccordion({
  heading,
  subheading,
  items = [],
  faqs,
  className,
}: Props) {
  const { lang, dir } = useLanguage();

  const list: FAQAccordionItem[] = faqs?.length
    ? [...faqs]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((f) => ({
          id: f.id,
          question: lang === "ar" && f.questionAr ? f.questionAr : f.question,
          answer: lang === "ar" && f.answerAr ? f.answerAr : f.answer,
        }))
    : items;

  if (list.length === 0) return null;

  const headingId = heading ? "faq-section-heading" : undefined;

  return (
    <section
      className={cn(
        "mx-auto max-w-3xl px-4 py-[var(--section-space)] md:px-6",
        className
      )}
      dir={dir}
      aria-labelledby={headingId}
    >
      {heading ? (
        <h2
          id={headingId}
          className="text-balance text-center text-3xl font-extrabold tracking-tight text-[var(--primary)] md:text-4xl"
        >
          {heading}
        </h2>
      ) : null}
      {subheading ? (
        <p className="mx-auto mt-3 max-w-xl text-center text-pretty text-slate-600">
          {subheading}
        </p>
      ) : null}

      <div
        className={cn("space-y-3", heading || subheading ? "mt-10" : "")}
        role="list"
      >
        {list.map((item, idx) => (
          <details
            key={item.id ?? idx}
            className="group rounded-2xl border border-slate-200 bg-white shadow-sm open:shadow-md"
            role="listitem"
          >
            <summary className="flex cursor-pointer list-none items-start justify-between gap-4 px-5 py-4 font-semibold text-[var(--primary)] [&::-webkit-details-marker]:hidden">
              <span className="text-pretty pe-2">{item.question}</span>
              <span
                className="mt-0.5 shrink-0 text-lg leading-none text-[var(--accent)] transition-transform duration-200 group-open:rotate-45"
                aria-hidden
              >
                +
              </span>
            </summary>
            <div className="border-t border-slate-100 px-5 pb-5 pt-1">
              <p className="text-pretty text-sm leading-relaxed text-slate-600">
                {item.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
